import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  PlayArrow,
  Assignment,
  Edit,
  FiberManualRecord,
  Close,
} from '@mui/icons-material';

interface Activity {
  type: 'audit_started' | 'audit_completed' | 'task_created' | 'task_updated' | 'task_completed';
  timestamp: string;
  audit_name?: string;
  task_title?: string;
  user_name?: string;
  assigned_to?: string;
  audit_id?: number;
  task_id?: number;
}

interface Week {
  label: string;
  start: string;
}

interface Client {
  id: number;
  name: string;
}

interface ActivityMatrixData {
  clients: Client[];
  weeks: Week[];
  activities: {
    [companyId: string]: {  // JSON serializes integer keys as strings
      [weekIndex: number]: Activity[];
    };
  };
  calculated_at: string;
}

interface ClientActivityMatrixProps {
  data: ActivityMatrixData | null;
  isLoading: boolean;
  error: string | null;
}

interface SelectedCell {
  companyId: number;
  companyName: string;
  weekIndex: number;
  weekLabel: string;
  activities: Activity[];
}

const ClientActivityMatrix: React.FC<ClientActivityMatrixProps> = ({ data, isLoading, error }) => {
  const navigate = useNavigate();
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);

  const getActivityIcon = (type: string, small: boolean = false) => {
    const size = small ? 14 : 16;
    switch (type) {
      case 'audit_started':
        return <PlayArrow sx={{ fontSize: size, color: 'info.main' }} />;
      case 'audit_completed':
        return <CheckCircle sx={{ fontSize: size, color: 'success.main' }} />;
      case 'task_created':
        return <Assignment sx={{ fontSize: size, color: 'warning.main' }} />;
      case 'task_updated':
        return <Edit sx={{ fontSize: size, color: 'text.secondary' }} />;
      case 'task_completed':
        return <CheckCircle sx={{ fontSize: size, color: 'success.main' }} />;
      default:
        return <FiberManualRecord sx={{ fontSize: size, color: 'action.disabled' }} />;
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case 'audit_started':
        return 'Review Started';
      case 'audit_completed':
        return 'Review Completed';
      case 'task_created':
        return 'Task Created';
      case 'task_updated':
        return 'Task Updated';
      case 'task_completed':
        return 'Task Completed';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type: string): string => {
    switch (type) {
      case 'audit_started':
        return '#1976d2'; // info.main
      case 'audit_completed':
        return '#2e7d32'; // success.main
      case 'task_created':
        return '#ed6c02'; // warning.main
      case 'task_updated':
        return '#757575'; // text.secondary
      case 'task_completed':
        return '#2e7d32'; // success.main
      default:
        return '#bdbdbd';
    }
  };

  const handleCellClick = (companyId: number, companyName: string, weekIndex: number) => {
    if (!data) return;
    
    // Convert companyId to string to match JSON serialized keys
    const cellActivities = data.activities[String(companyId)]?.[weekIndex] || [];
    
    if (cellActivities.length === 0) return; // Don't select empty cells
    
    // If clicking the same cell, deselect it
    if (selectedCell?.companyId === companyId && selectedCell?.weekIndex === weekIndex) {
      setSelectedCell(null);
      return;
    }
    
    setSelectedCell({
      companyId,
      companyName,
      weekIndex,
      weekLabel: data.weeks[weekIndex].label,
      activities: cellActivities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    });
  };

  const renderCell = (client: Client, weekIndex: number) => {
    if (!data) return null;

    // Convert client.id to string to match JSON serialized keys
    const cellActivities = data.activities[String(client.id)]?.[weekIndex] || [];
    const hasActivity = cellActivities.length > 0;
    const isSelected = selectedCell?.companyId === client.id && selectedCell?.weekIndex === weekIndex;

    // Count activity types for color intensity
    const activityCounts = cellActivities.reduce((acc, activity) => {
      acc[activity.type] = (acc[activity.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Determine cell background color based on activity - dark mode compatible
    // Priority: Open tasks (yellow) > Started audits (blue) > Completed (green) > Updates (grey)
    let backgroundColor = 'transparent'; // Empty cells transparent
    if (hasActivity) {
      // If there are any open/created tasks, show yellow (highest priority)
      if (activityCounts['task_created']) {
        backgroundColor = 'rgba(255, 193, 7, 0.35)'; // warning - brighter
      } 
      // Else if audit started (but no open tasks), show blue
      else if (activityCounts['audit_started']) {
        backgroundColor = 'rgba(25, 118, 210, 0.35)'; // info - brighter
      } 
      // Else if only completed activities (no open tasks, no started audits), show green
      else if (activityCounts['audit_completed'] || activityCounts['task_completed']) {
        backgroundColor = 'rgba(67, 160, 71, 0.35)'; // success - brighter
      } 
      // Else just updates, show subtle grey
      else {
        backgroundColor = 'rgba(255, 255, 255, 0.12)'; // brighter grey for updates
      }
    }

    return (
      <Box
        onClick={() => hasActivity && handleCellClick(client.id, client.name, weekIndex)}
        sx={{
          width: '100%',
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isSelected ? 'rgba(229, 57, 53, 0.16)' : backgroundColor,
          cursor: hasActivity ? 'pointer' : 'default',
          border: isSelected ? '2px solid' : 'none',
          borderColor: 'primary.main',
          transition: 'all 0.2s',
          '&:hover': hasActivity ? {
            backgroundColor: isSelected ? 'rgba(229, 57, 53, 0.2)' : 'rgba(255, 255, 255, 0.05)',
            boxShadow: 1,
          } : {},
        }}
      >
        {hasActivity && (
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {cellActivities.length}
          </Typography>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading client activity...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.clients.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">No client activity data available</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Client Activity Matrix
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 12 weeks • Click a cell to see details
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: 'rgba(67, 160, 71, 0.35)', border: 1, borderColor: 'divider' }} />
                <Typography variant="caption">Completed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: 'rgba(25, 118, 210, 0.35)', border: 1, borderColor: 'divider' }} />
                <Typography variant="caption">Started</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: 'rgba(255, 193, 7, 0.35)', border: 1, borderColor: 'divider' }} />
                <Typography variant="caption">Tasks</Typography>
              </Box>
            </Box>
          </Box>

          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
            }}
          >
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Client</TableCell>
                  {data.weeks.map((week, idx) => (
                    <TableCell key={idx} align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>
                      {week.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.clients.map((client) => (
                  <TableRow
                    key={client.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main' },
                      }}
                      onClick={() => navigate(`/company/${client.id}`)}
                    >
                      {client.name}
                    </TableCell>
                    {data.weeks.map((week, weekIdx) => (
                      <TableCell key={weekIdx} align="center" sx={{ p: 0 }}>
                        {renderCell(client, weekIdx)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Activity Detail Table */}
      <Collapse in={selectedCell !== null} timeout="auto">
        {selectedCell && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6">
                    {selectedCell.companyName} - Week of {selectedCell.weekLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedCell.activities.length} {selectedCell.activities.length === 1 ? 'activity' : 'activities'}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectedCell(null)}>
                  <Close />
                </IconButton>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="15%">Type</TableCell>
                      <TableCell width="25%">Name</TableCell>
                      <TableCell width="20%">Review Name</TableCell>
                      <TableCell width="15%">User</TableCell>
                      <TableCell width="25%">Date & Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCell.activities.map((activity, idx) => {
                      const isAudit = activity.type === 'audit_started' || activity.type === 'audit_completed';
                      const isTask = activity.type === 'task_created' || activity.type === 'task_updated' || activity.type === 'task_completed';
                      
                      return (
                        <TableRow key={idx} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getActivityIcon(activity.type, true)}
                              <Typography variant="body2">
                                {getActivityLabel(activity.type).replace(' Started', '').replace(' Completed', '').replace(' Created', '').replace(' Updated', '')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {isAudit ? (activity.audit_name || 'Unnamed Audit') : (activity.task_title || 'Untitled Task')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {activity.audit_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {isTask ? (activity.assigned_to || 'Unassigned') : (activity.user_name || '-')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(activity.timestamp).toLocaleString('en-AU', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                            <Chip
                              label={
                                activity.type.includes('completed') ? 'Completed' :
                                activity.type.includes('started') ? 'Started' :
                                activity.type.includes('created') ? 'Created' :
                                'Updated'
                              }
                              size="small"
                              color={
                                activity.type.includes('completed') ? 'success' :
                                activity.type.includes('started') ? 'info' :
                                activity.type.includes('created') ? 'warning' :
                                'default'
                              }
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Collapse>
    </Box>
  );
};

export default ClientActivityMatrix;
